import { AssistantEvent } from '../../../common/hooks/use-assistant-api.hook';

/**
 * The turn in progress, built from the stream as it arrives.
 *
 * The assistant used to show one italic line — the tool running right now —
 * which is the least a working agent can show and reads as a spinner with
 * words. This keeps everything the stream says: every step the lead took, and
 * every specialist it sent work to with that specialist's own steps and, when
 * it finishes, its report. The conversation draws it live; once the reply is
 * saved the thread takes over and this is discarded.
 *
 * Pure, so the order-of-events rules are tested rather than eyeballed.
 */

export type LiveStepStatus = 'running' | 'done' | 'failed';

export interface LiveStep {
    readonly tool: string;
    readonly status: LiveStepStatus;
}

export type FindingTone = 'good' | 'risk' | 'info';

export interface TeamReport {
    readonly headline: string;
    readonly findings: ReadonlyArray<{ title: string; detail: string; tone: FindingTone; figure?: string }>;
    readonly next?: string;
}

export interface LiveMember {
    readonly runId: string;
    readonly agent: string;
    readonly name: string;
    readonly brief: string;
    readonly status: 'working' | 'done' | 'failed';
    readonly steps: readonly LiveStep[];
    readonly report?: TeamReport;
    readonly error?: string;
}

export interface LiveTurn {
    readonly runId?: string;
    readonly startedAt: number;
    /** The lead's own steps, in order. `ask_team` is shown as the team, not as a step. */
    readonly steps: readonly LiveStep[];
    readonly team: readonly LiveMember[];
}

/** The tool that hands work to the team — drawn as the team, never as a step. */
export const TEAM_TOOL = 'ask_team';

export const startTurn = (now = Date.now()): LiveTurn => ({ startedAt: now, steps: [], team: [] });

/** Mark the newest running step with this tool as finished. */
function settle(steps: readonly LiveStep[], tool: string, status: LiveStepStatus): LiveStep[] {
    const out = [...steps];
    for (let i = out.length - 1; i >= 0; i--) {
        if (out[i].tool === tool && out[i].status === 'running') {
            out[i] = { ...out[i], status };
            break;
        }
    }
    return out;
}

function member(turn: LiveTurn, runId: string, change: (m: LiveMember) => LiveMember): LiveTurn {
    return { ...turn, team: turn.team.map((m) => (m.runId === runId ? change(m) : m)) };
}

export function reduceLive(turn: LiveTurn, event: AssistantEvent): LiveTurn {
    const data = event.data ?? {};

    switch (event.type) {
        case 'thinking':
            return data.runId && !turn.runId ? { ...turn, runId: String(data.runId) } : turn;

        case 'tool_call': {
            const tool = String(data.tool ?? '');
            if (!tool || tool === TEAM_TOOL) return turn;
            return { ...turn, steps: [...turn.steps, { tool, status: 'running' }] };
        }

        case 'tool_result': {
            const tool = String(data.tool ?? '');
            if (!tool || tool === TEAM_TOOL) return turn;
            return { ...turn, steps: settle(turn.steps, tool, data.error ? 'failed' : 'done') };
        }

        case 'team': {
            const runId = String(data.runId ?? '');
            if (!runId) return turn;

            switch (data.phase) {
                case 'started':
                    if (turn.team.some((m) => m.runId === runId)) return turn;
                    return {
                        ...turn,
                        team: [...turn.team, {
                            runId,
                            agent: String(data.agent ?? ''),
                            name: String(data.name ?? ''),
                            brief: String(data.brief ?? ''),
                            status: 'working',
                            steps: [],
                        }],
                    };
                case 'step':
                    return member(turn, runId, (m) => ({ ...m, steps: [...m.steps, { tool: String(data.tool ?? ''), status: 'running' }] }));
                case 'step_done':
                    return member(turn, runId, (m) => ({ ...m, steps: settle(m.steps, String(data.tool ?? ''), data.ok === false ? 'failed' : 'done') }));
                case 'finished':
                    return member(turn, runId, (m) => ({
                        ...m,
                        status: 'done',
                        report: data.report,
                        steps: m.steps.map((s) => (s.status === 'running' ? { ...s, status: 'done' } : s)),
                    }));
                case 'failed':
                    return member(turn, runId, (m) => ({
                        ...m,
                        status: 'failed',
                        error: String(data.error ?? ''),
                        steps: m.steps.map((s) => (s.status === 'running' ? { ...s, status: 'failed' } : s)),
                    }));
                default:
                    return turn;
            }
        }

        default:
            return turn;
    }
}

/** How many steps the whole turn has taken so far — the lead's and the team's. */
export const stepCount = (turn: LiveTurn): number =>
    turn.steps.length + turn.team.reduce((n, m) => n + m.steps.length, 0);
